import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useLocation } from 'wouter';

import { ACCOUNT_PATHS, accountPageInSprint, type AccountPageId } from '@/lib/accountPaths';
import { useAccountRecord } from './AccountRecordContext';
import { accountCount } from './accountModel';
import { StatusChip } from './primitives';

/**
 * AR-380 — what the overview page carried, on every Account page instead
 * (D-ACCT-25). v15 removed the overview, so the items that needed attention had
 * nowhere to live; they ride above every page now, and minimise to one line.
 *
 * The rules are the items' own: with nothing to act on, nothing renders. Each
 * item derives from a record the center can show, so the notice can never name
 * a task the pages do not support — and never points at a page this sprint does
 * not carry.
 */

export interface AccountAction {
  text: string;
  status: string;
  page: AccountPageId;
  door: string;
}

const NOTICE_KEY = 'aivia-account-notice-min';

export function useAccountActions(): AccountAction[] {
  const { record } = useAccountRecord();
  const out: AccountAction[] = [];

  if (accountPageInSprint('documents')) {
    out.push({
      text: 'Add your business continuity designation',
      status: 'Not on file',
      page: 'documents',
      door: 'Documents',
    });
  }
  if (!String(record.biz.desc ?? '').trim()) {
    out.push({
      text: 'Complete your Business Description',
      status: 'Incomplete',
      page: 'public',
      door: 'Brand + Public Presence',
    });
  }
  if (record.pubmeta.dirty) {
    out.push({
      text: 'Publish the changes to your public profile',
      status: 'Draft',
      page: 'public',
      door: 'Brand + Public Presence',
    });
  }
  return out;
}

const readMinimised = (): boolean => {
  try {
    return localStorage.getItem(NOTICE_KEY) === '1';
  } catch {
    return false;
  }
};

export const AccountNotice: React.FC = () => {
  const [, navigate] = useLocation();
  const actions = useAccountActions();
  // Minimising is this browser's choice, not the record's.
  const [minimised, setMinimised] = React.useState(readMinimised);

  const setMin = (next: boolean) => {
    setMinimised(next);
    try {
      localStorage.setItem(NOTICE_KEY, next ? '1' : '0');
    } catch {
      // A browser that will not store the preference still gets the toggle.
    }
  };

  if (!actions.length) return null;

  // On the theme gradient, so it reads as chrome above the page rather than as
  // another card in the page's own stack.
  const shell =
    'my-3 rounded-lg border border-orange-300 bg-gradient-to-r from-orange-50 to-white px-4 dark:border-orange-800 dark:from-orange-950/40 dark:to-transparent';

  if (minimised) {
    return (
      <div data-account-notice="min" className={`${shell} flex items-center gap-3 py-2`}>
        <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
          Action required
        </span>
        <span className="flex-1 text-[14px] text-gray-700 dark:text-gray-300">
          {accountCount(actions.length, 'item', 'items')}
        </span>
        <button
          type="button"
          onClick={() => setMin(false)}
          title="Show"
          aria-label="Show the items"
          aria-expanded={false}
          className="rounded p-1 text-orange-700 hover:bg-orange-100 dark:text-orange-400 dark:hover:bg-orange-900/40"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div data-account-notice="open" className={`${shell} py-1.5`}>
      <div className="mb-1 flex items-baseline justify-between gap-3 pt-1.5">
        <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
          Action required
        </span>
        <button
          type="button"
          onClick={() => setMin(true)}
          title="Minimise"
          aria-label="Minimise to one line"
          aria-expanded
          className="rounded p-1 text-orange-700 hover:bg-orange-100 dark:text-orange-400 dark:hover:bg-orange-900/40"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
      </div>
      {actions.map((a, i) => (
        <div
          key={a.text}
          className={`flex flex-wrap items-center gap-3 py-2.5 ${
            i === actions.length - 1
              ? ''
              : 'border-b border-orange-200/60 dark:border-orange-900/40'
          }`}
        >
          <span className="w-full font-semibold text-gray-900 dark:text-gray-100 sm:w-auto sm:min-w-0 sm:flex-1">
            {a.text}
          </span>
          <StatusChip status={a.status} />
          <span className="flex-none">
            <button
              type="button"
              onClick={() => navigate(ACCOUNT_PATHS[a.page])}
              className="text-orange-700 hover:underline dark:text-orange-400"
            >
              {a.door} →
            </button>
          </span>
        </div>
      ))}
    </div>
  );
};
